import {toInt} from '@/library/math';
import axios from 'axios';
import {getUsername} from '../../../filters/username';
import store from '../../../store';
import {XHR_STATUS_DEFAULT, XHR_STATUS_INVALID} from '../../../utils/portuna';
import {parseFormData} from '../../form';
import TopMessageHandler from '../../topMessageHandler';

export const CHANGING_REASON_START_VALUE = '0';
export const CHANGING_REASON_CHANGED = '1';
export const CHANGING_REASON_OVERTIME = '2';
export const CHANGING_REASON_UNDER_TIME = '3';
export const CHANGING_REASON_OVERTIME_CUT = '4';
export const WORK_BEGIN  = 1;
export const WORK_END    = 2;
export const BREAK_BEGIN = 3;
export const BREAK_END   = 4;
export const CORRECTION_ADD_WORK_TIME_AND_BREAKS = 6;

export const PATROL_PROJECT = -1;

export const PATROL_BEGIN = 'patrolBegin';
export const PATROL_END   = 'patrolEnd';

export const SYSTEM_BROWSER = 1;
export const SYSTEM_APP = 2;
export const SYSTEM_TERMINAL= 3;

export const MODE_MANUAL     = 1;
export const MODE_TIME_CLOCK = 1 << 1;
export const MODE_ABSENCES   = 1 << 2;

export const LOCATION_AVAILABILITY_AVAILABLE = 0;
export const LOCATION_AVAILABILITY_UNAVAILABLE = 1;
export const LOCATION_AVAILABILITY_ADDRESS_NOT_FOUND = 2;
export const LOCATION_AVAILABILITY_NOT_FOUND = 3;

export const timeTrackerEntryTypes = {
    1: 'Arbeitsbeginn',
    2: 'Arbeitsende',
    3: 'Pausenbeginn',
    4: 'Pausenende',
};

export const TIMETRACKER_SYSTEM_ICONS = {
    [SYSTEM_BROWSER]: {
        tooltip: 'Anmeldung über den Browser',
        tooltipCapture: 'Erfassung via Browser',
        tooltipBreak: 'Anmeldung zur Pause über den Browser',
        icon: 'computer',
    },
    [SYSTEM_APP]: {
        tooltip: 'Anmeldung über die App',
        tooltipCapture: 'Erfassung via App',
        tooltipBreak: 'Anmeldung zur Pause über die App',
        icon: 'smartphone',
    },
    [SYSTEM_TERMINAL]: {
        tooltip: 'Anmeldung über das Terminal',
        tooltipCapture: 'Erfassung via Terminal',
        tooltipBreak: 'Anmeldung zur Pause über das Terminal',
        icon: 'nfc',
    },
};

export const NOTE_WEEK = 'week';

export const NOTE_DAY = 'day';

export const POPUP_ACTION_MODE_ACCEPT  = 1;
export const POPUP_ACTION_MODE_DECLINE = 2;
export const POPUP_ACTION_MODE_BOTH    = 3;

export const SETTINGS_WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

export const SETTINGS_WORKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export function getAssignmentTooltip(assignments: number[], type: 'employee' | 'entity')
{
    return assignments.map(assignments => getUsername(assignments, type)).join(', ');
}

export function saveTimeTrackerNote(formData: { note: string }, noteId: number, employeeId: number, date: string, type: string = NOTE_WEEK): Promise<NoteEntry>
{
    return new Promise((resolve, reject) =>
    {
        axios.post(`/employee/time-tracker/evaluation/save-note/id/${noteId}/employee-id/${employeeId}/date/${date}/type/${type}`, parseFormData(formData))
        .then(response =>
        {
            const data = response.data;

            if (data.status !== XHR_STATUS_DEFAULT)
            {
                throw response;
            }

            TopMessageHandler.success((type === NOTE_WEEK ? 'Die Notiz' : 'Der Kommentar') + ' wurde erfolgreich gespeichert.');

            resolve(data.data.entry);
        })
        .catch(error =>
        {
            TopMessageHandler.error(error?.data?.status === XHR_STATUS_INVALID
                ? 'Bitte überprüfen Sie Ihre Eingaben!'
                : 'Beim Speichern ist ein Fehler aufgetreten! Bitte versuchen Sie es später noch einmal.');

            reject(error);
        });
    });
}

export function hasManualMode()
{
    return !!(store.state.layoutData.employee_configuration!.time_tracker_mode & MODE_MANUAL);
}

export function isManualModeEmployee(systemSpecific: boolean = true)
{
    if (systemSpecific)
    {
        return !!(store.state.layoutData.employee_configuration!.time_tracker_mode_employee_system & MODE_MANUAL);
    }

    return !!(store.state.layoutData.employee_configuration!.time_tracker_mode_employee & MODE_MANUAL);
}

export function hasTimeClockMode()
{
    return !!(store.state.layoutData.employee_configuration!.time_tracker_mode & MODE_TIME_CLOCK);
}

export function isTimeClockModeEmployee(systemSpecific: boolean = true)
{
    if (systemSpecific)
    {
        return !!(store.state.layoutData.employee_configuration!.time_tracker_mode_employee_system & MODE_TIME_CLOCK);
    }

    return !!(store.state.layoutData.employee_configuration!.time_tracker_mode_employee & MODE_TIME_CLOCK);
}

export function isAbsenceMode()
{
    return !!(store.state.layoutData.employee_configuration!.time_tracker_mode & MODE_ABSENCES);
}

export function getSecondsByTimeString(time: string)
{
    if (time.match(/\d+[.,]\d+/))
    {
        let decimal = toInt(time);

        time = Math.floor(decimal) + ':' + (decimal % 1 * 60);
    }

    const timeParts = time.match(/(\d{1,2})(?::(\d{1,2}))?/);

    if (!timeParts)
    {
        return 0;
    }

    let hours   = parseInt(timeParts[1]);
    let minutes = parseInt(timeParts[2] ?? 0);

    if (hours >= 24)
    {
        hours   = 24;
        minutes = 0;
    }

    return Math.min(hours * 3_600 + Math.min(minutes, 60) * 60, 86_400);
}

export function reformatTime(totalSeconds: number)
{
    // abs is required for correct handling
    // e.g. Math.floor(-0.2) === -1 (not 0 which is required)
    totalSeconds = Math.abs(totalSeconds);

    let hours: string | number   = Math.floor(totalSeconds / 3_600);
    totalSeconds %= 3_600;
    let minutes: string | number = Math.floor(totalSeconds / 60);

    if (hours < 10)
    {
        hours = '0' + hours;
    }
    if (minutes < 10)
    {
        minutes = '0' + minutes;
    }

    return `${hours}:${minutes}`;
}

export function checkHasDayWorkTimeSettings(settings: TimeTrackerSettings, lowerDay: TDayNames): boolean
{
    return !!(
        settings[`general_worktime_${lowerDay}`]
        || settings['general_worktime_all'] && SETTINGS_WORKDAYS.includes(lowerDay)
        || settings['general_worktime_workdays'] && SETTINGS_WEEKDAYS.includes(lowerDay)
    );
}